import { useRef, useState, type FormEvent } from 'react';
import { isAxiosError } from 'axios';
import { Alert, Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import type { SkinAnalysisReportLimitResetResult } from '@/models/SkinAnalysisReportLimitReset';

interface SkinAnalysisReportResetCardProps {
  onReset: (email: string) => Promise<SkinAnalysisReportLimitResetResult>;
}

function toErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown } | undefined;
    if (typeof data?.message === 'string') {
      return data.message;
    }
  }
  return error instanceof Error && error.message ? error.message : 'Reset failed';
}

/** Deletes every Skin Analysis row (surveys included) of the accounts registered with an email. */
export function SkinAnalysisReportResetCard({ onReset }: SkinAnalysisReportResetCardProps) {
  const [email, setEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [result, setResult] = useState<SkinAnalysisReportLimitResetResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // State updates land after the next render, so a ref blocks a second click in the same tick.
  const inFlight = useRef(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = email.trim();
    if (inFlight.current || !target) {
      return;
    }
    if (
      !window.confirm(`Delete every Skin Analysis report and survey for ${target}? The customer must redo the survey.`)
    ) {
      return;
    }

    inFlight.current = true;
    setIsResetting(true);
    setResult(null);
    setError(null);
    try {
      setResult(await onReset(target));
    } catch (resetError) {
      setError(toErrorMessage(resetError));
    } finally {
      inFlight.current = false;
      setIsResetting(false);
    }
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title as="h6">Skin Analysis report reset</Card.Title>
        <Form onSubmit={submit}>
          <InputGroup>
            <Form.Control
              type="email"
              aria-label="Customer email"
              placeholder="customer@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={isResetting}
              required
            />
            <Button type="submit" variant="danger" disabled={isResetting || !email.trim()}>
              {isResetting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Resetting…
                </>
              ) : (
                'Reset'
              )}
            </Button>
          </InputGroup>
        </Form>
        {isResetting && (
          <div role="status" className="small text-muted mt-2">
            Deleting reports and surveys. This can take over a minute; keep this page open.
          </div>
        )}
        {error && (
          <Alert variant="danger" className="mt-2 mb-0">
            {error}
          </Alert>
        )}
        {result && (
          <Alert variant="success" className="mt-2 mb-0">
            {result.users.map((user) => (
              <div key={user.customerId}>
                Customer {user.customerId}: {user.deletedRows} rows deleted
              </div>
            ))}
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}
